import React from "react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Cross2Icon, PlusIcon } from "@radix-ui/react-icons";
import { CheckCircle2, X, Download, Users } from "lucide-react";
import { DataTableViewOptions } from "./data-table-view-options";
import { Table } from "@tanstack/react-table";
import { FormProvider, useForm } from "react-hook-form";
import SelectController from "../form-fields/SelectController"; // ✅ Import SelectController
import ComboboxController from "../form-fields/ComboboxController"; // ✅ Import ComboboxController
import federalAgencies from "../../utils/federal_agencies.json";
import { getStates } from "../../lib/utils";
import { useNavigate } from "react-router-dom";
import { AddClientModal } from "./add-client-modal";
import { MassDelete } from "./mass-delete";
import { ExportClientButton } from "../clients/export-client-button";
import { ImportClientsModal } from "../clients/import-clients-modal";
import { ReassignClientModal } from "../clients/reassign-client-modal";
import { useAuth } from "../../lib/AuthContext";

interface DataTableToolbarProps<TData> {
  table: Table<TData>;
  search: string;
  setSearch: (value: string) => void;
  statusFilter: string;
  setStatusFilter: (value: string) => void;
  lifecycleStage: string;
  setLifecycleStage: (value: string) => void;
  agencyFilter: string;
  setAgencyFilter: (value: string) => void;
  stateFilter: string;
  setStateFilter: (value: string) => void;
  userFilter: string;
  setUserFilter: (value: string) => void;
  agents: any[];
  isAdmin: boolean;
  selectedCount?: number;
  selectedIds?: string[];
  selectedClients?: TData[];
}

export function DataTableToolbar<TData>({
  table,
  search,
  setSearch,
  statusFilter,
  setStatusFilter,
  lifecycleStage,
  setLifecycleStage,
  agencyFilter,
  setAgencyFilter,
  stateFilter,
  setStateFilter,
  userFilter,
  setUserFilter,
  agents,
  isAdmin,
  selectedCount = 0,
  selectedIds = [],
  selectedClients = [],
}: DataTableToolbarProps<TData>) {
  const formMethods = useForm(); // ✅ Form Context for Controlled Inputs
  const navigate = useNavigate();
  const auth = useAuth();
  
  const canDelete = auth?.currentUser?.user_permissions?.[0]?.can_manage_clients;

  // ✅ Transform agents (members) into options for dropdown
  const agentOptions = React.useMemo(() => {
    if (!agents || agents.length === 0)
      return [{ value: "", label: "All Agents" }];

    console.log("🔧 Transforming agents for dropdown:", agents);

    return [
      { value: "", label: "All Agents" },
      ...agents.map((member: any) => ({
        value: String(member.id),
        label: member.name || `User ${member.id}`,
      })),
    ];
  }, [agents]);

  return (
    <FormProvider {...formMethods}>
      <div className="flex flex-col gap-4">
        {/* First Row - Action Buttons */}
        <div className="flex items-center justify-between px-5">
          {/* Left side - Selection info (when items selected) */}
          <div className="flex items-center gap-3">
            {selectedCount > 0 && (
              <>
                <div className="bg-accent rounded-full p-1.5">
                  <CheckCircle2 className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-900">
                    {selectedCount} {selectedCount === 1 ? 'Client' : 'Clients'} Selected
                  </p>
                </div>
              </>
            )}
          </div>
          
          {/* Right side - Action buttons */}
          <div className="flex gap-2">
            {/* Export Button - Show when items selected */}
            {selectedCount > 0 && (
              <ExportClientButton 
                clients={selectedClients as any}
              >
                <Button variant="outline" size="sm" className="border-accent text-accent hover:bg-accent hover:text-accent-foreground">
                  <Download className="h-4 w-4 mr-2" />
                  Export ({selectedCount})
                </Button>
              </ExportClientButton>
            )}
            
            {/* Reassign Button - Show when items selected and user is admin */}
            {selectedCount > 0 && isAdmin && (
              <ReassignClientModal
                clients={selectedClients as any}
                onSuccess={() => {
                  table.toggleAllRowsSelected(false);
                }}
                trigger={
                  <Button variant="outline" size="sm" className="border-accent text-accent hover:bg-accent">
                    <Users className="h-4 w-4 mr-2" />
                    Reassign ({selectedCount})
                  </Button>
                }
              />
            )}
            
            {/* Mass Delete - Always visible if user has permission, disabled when nothing selected */}
            {canDelete && (
              <MassDelete 
                selectedIds={selectedIds}
                onSuccess={() => {
                  table.toggleAllRowsSelected(false);
                }}
              />
            )}
            
            {/* Clear Selection - Show when items selected */}
            {selectedCount > 0 && (
              <Button 
                variant="ghost"
                size="sm"
                onClick={() => table.toggleAllRowsSelected(false)}
                className="hover:bg-accent" 
              >
                <X className="h-4 w-4" />
              </Button>
            )}
            
            {/* Import Button - Show when no items selected */}
            {selectedCount === 0 && canDelete && (
              <ImportClientsModal 
                onSuccess={() => {
                  // Refresh the page or trigger a data refetch
                  window.location.reload();
                }}
              />
            )}
            
            {/* Add Client Button */}
            <AddClientModal />
          </div>
        </div>

        {/* ✅ Filters Section - Inline with Improved Spacing */}
        <div className="px-6 py-4 rounded-lg border bg-gray-50/50">
          <div className="flex items-center gap-3">
            {/* Search Input */}
            <div className="flex flex-col flex-1 min-w-[200px]">
              <label className="text-xs font-medium text-gray-700 mb-2 block">
                Search by Last Name
              </label>
              <Input
                placeholder="Search..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-9"
              />
            </div>

            {/* Status Filter */}
            <div className="flex flex-col w-[140px]">
              <label className="text-xs font-medium text-gray-700 mb-2 block">
                Status
              </label>
              <SelectController
                name="statusFilter"
                label=""
                options={[
                  { value: "", label: "All" },
                  { value: "Active", label: "Active" },
                  { value: "Inactive", label: "Inactive" },
                  { value: "Pending", label: "Pending" },
                ]}
                defaultValue={statusFilter}
                onChange={(value) => {
                  console.log("🔧 Status filter changed to:", value);
                  setStatusFilter(value);
                }}
              />
            </div>

            {/* Lifecycle Stage Filter */}
            <div className="flex flex-col w-[160px]">
              <label className="text-xs font-medium text-gray-700 mb-2 block">
                Lifecycle Stage
              </label>
              <SelectController
                name="lifecycleStage"
                label=""
                options={[
                  { value: "", label: "All" },
                  { value: "prospect", label: "Prospect" },
                  { value: "inital_outreach", label: "Initial Outreach" },
                  { value: "benefits_analysis", label: "Benefits Analysis" },
                  { value: "proposal_sent", label: "Proposal Sent" },
                  { value: "customer", label: "Customer" },
                  { value: "lost", label: "Lost" },
                ]}
                defaultValue={lifecycleStage}
                onChange={(value) => {
                  console.log("🔧 Lifecycle stage filter changed to:", value);
                  setLifecycleStage(value);
                }}
              />
            </div>

            {/* Agency Filter */}
            <div className="flex flex-col flex-1 min-w-[180px]">
              <label className="text-xs font-medium text-gray-700 mb-2 block">
                Agency
              </label>
              <ComboboxController
                name="agencyFilter"
                label=""
                options={federalAgencies}
                defaultValue={agencyFilter}
                multiple={false}
                onChange={(selected) => {
                  if (typeof selected === "string") {
                    setAgencyFilter(selected);
                  }
                }}
              />
            </div>

            {/* State Filter */}
            <div className="flex flex-col w-[120px]">
              <label className="text-xs font-medium text-gray-700 mb-2 block">
                State
              </label>
              <SelectController
                name="stateFilter"
                label=""
                options={getStates()}
                defaultValue={stateFilter}
                onChange={setStateFilter}
              />
            </div>

            {/* Agent Filter - Admin Only */}
            {isAdmin && (
              <div className="flex flex-col w-[140px]">
                <label className="text-xs font-medium text-gray-700 mb-2 block">
                  Agent
                </label>
                <SelectController
                  name="userFilter"
                  label=""
                  options={agentOptions}
                  defaultValue={userFilter}
                  onChange={(value) => {
                    console.log("🔧 Agent filter changed to:", value);
                    setUserFilter(value);
                  }}
                />
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col">
              <label className="text-xs font-medium text-gray-700 mb-2 block invisible">
                Actions
              </label>
              <div className="flex gap-2">
                {/* Reset Filters */}
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9"
                  onClick={() => {
                    setSearch("");
                    setStatusFilter("");
                    setLifecycleStage("");
                    setAgencyFilter("");
                    setStateFilter("");
                    setUserFilter("");
                  }}
                >
                  Reset
                  <Cross2Icon className="ml-2 h-4 w-4" />
                </Button>

                {/* Table View Options */}
                <DataTableViewOptions table={table} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </FormProvider>
  );
}
